// src/rules.ts
var DEFAULT_RULES = {
  gps: {
    maxAccuracyForDistanceM: 50,
    maxAccuracyForCaptureM: 25,
    maxSpeedMps: 8.5,
    jumpDistanceM: 100,
    minMoveM: 4,
    maxMinMoveM: 20,
    minMoveAccuracyFactor: 0.8,
    reanchorAfter: 4,
    futureToleranceMs: 2 * 6e4,
    runWindowSlackMs: 6e4
  },
  validation: {
    vehicleAvgSpeedMps: 6.5,
    vehicleBurstSpeedMps: 7.5,
    vehicleBurstShare: 0.3,
    suspiciousRejectShare: 0.15,
    suspiciousJumpCount: 6,
    rejectFutureShare: 0.2,
    suspiciousBackwardCount: 3,
    suspiciousOutOfRunShare: 0.05,
    suspiciousInvalidShare: 0.05,
    clientMismatchMinM: 100,
    clientMismatchShare: 0.25,
    syntheticMinPoints: 30,
    syntheticSpeedStdMps: 0.02,
    lowQualityShare: 0.5,
    minInsideRegionShare: 0.5
  },
  capture: {
    minDistanceInsideM: 30,
    sampleStepM: 5
  },
  xp: {
    perKm: 10,
    territoryCapture: 100,
    firstDailyRun: 50,
    completedRun: 25,
    sponsorCell: 50,
    completedMinDistanceM: 200,
    completedMinDurationS: 60
  },
  levels: {
    base: 250,
    exponent: 1,
    maxLevel: 200
  },
  ranks: {
    rookie: 1,
    bronze: 5,
    silver: 10,
    gold: 20,
    platinum: 35,
    diamond: 55,
    master: 80,
    legend: 120
  },
  badges: {
    tiers: {
      distance: [5e4, 25e4, 1e6],
      longest_run: [1e4, 21097, 42195],
      streak: [7, 30, 100],
      runs: [10, 50, 200],
      captures: [50, 500, 2e3],
      held: [25, 100, 300],
      steals: [10, 100, 500],
      revenge: [1, 10, 50],
      explorer: [100, 500, 2e3],
      early_bird: [5, 20, 50],
      night_owl: [5, 20, 50],
      events_completed: [1, 5, 15],
      event_podium: [1],
      team_champion: [1],
      organizer: [1],
      club_member: [1]
    },
    earlyBeforeHour: 5,
    nightFromHour: 21,
    timeOfDayMinRunM: 1e3,
    revengeWindowH: 24
  }
};
function createRules(overrides = {}) {
  return {
    gps: { ...DEFAULT_RULES.gps, ...overrides.gps },
    validation: { ...DEFAULT_RULES.validation, ...overrides.validation },
    capture: { ...DEFAULT_RULES.capture, ...overrides.capture },
    xp: { ...DEFAULT_RULES.xp, ...overrides.xp },
    levels: { ...DEFAULT_RULES.levels, ...overrides.levels },
    ranks: { ...DEFAULT_RULES.ranks, ...overrides.ranks },
    badges: {
      ...DEFAULT_RULES.badges,
      ...overrides.badges,
      tiers: { ...DEFAULT_RULES.badges.tiers, ...overrides.badges?.tiers }
    }
  };
}

// src/levels.ts
function xpToCompleteLevel(level, rules = DEFAULT_RULES) {
  return Math.round(rules.levels.base * Math.pow(level, rules.levels.exponent));
}
function totalXpForLevel(level, rules = DEFAULT_RULES) {
  let total = 0;
  for (let l = 1; l < level; l++) total += xpToCompleteLevel(l, rules);
  return total;
}
function levelProgress(totalXp, rules = DEFAULT_RULES) {
  const xp = Math.max(0, Math.floor(totalXp));
  let level = 1;
  let floor = 0;
  while (level < rules.levels.maxLevel) {
    const need = xpToCompleteLevel(level, rules);
    if (xp < floor + need) break;
    floor += need;
    level++;
  }
  return {
    level,
    totalXp: xp,
    xpIntoLevel: xp - floor,
    xpForLevel: xpToCompleteLevel(level, rules)
  };
}
function levelForXp(totalXp, rules = DEFAULT_RULES) {
  return levelProgress(totalXp, rules).level;
}

// src/achievements.ts
import { BADGE_IDS, RANK_IDS } from "@rw/shared";
function rankForLevel(level, rules = DEFAULT_RULES) {
  let rank = RANK_IDS[0];
  for (const id of RANK_IDS) if (level >= rules.ranks[id]) rank = id;
  return rank;
}
function rankUp(fromLevel, toLevel, rules = DEFAULT_RULES) {
  const from = rankForLevel(fromLevel, rules);
  const to = rankForLevel(toLevel, rules);
  return RANK_IDS.indexOf(to) > RANK_IDS.indexOf(from) ? to : null;
}
var REVOCABLE_BADGES = /* @__PURE__ */ new Set([
  "distance",
  "longest_run",
  "runs",
  "captures",
  "steals",
  "revenge",
  "explorer",
  "early_bird",
  "night_owl"
]);
function badgeTier(id, value, rules = DEFAULT_RULES) {
  let tier = 0;
  for (const need of rules.badges.tiers[id]) if (value >= need) tier++;
  return tier;
}
function badgeTiers(stats, rules = DEFAULT_RULES) {
  return Object.fromEntries(
    BADGE_IDS.map((id) => [id, badgeTier(id, stats[id] ?? 0, rules)])
  );
}

// src/trace.ts
import { haversineMeters, isValidCoordinate } from "@rw/geo";
var TraceProcessor = class {
  constructor(opts = {}) {
    this.opts = opts;
    this.rules = opts.rules ?? DEFAULT_RULES;
  }
  opts;
  rules;
  segments = [];
  counters = {
    received: 0,
    invalidCoordinates: 0,
    duplicates: 0,
    backward: 0,
    future: 0,
    outOfRun: 0,
    poorAccuracy: 0,
    jumps: 0,
    impossibleSpeed: 0,
    jitter: 0,
    reanchors: 0,
    movementCandidates: 0
  };
  /** Points involved in a jump (both ends) — used to disqualify captures. */
  jumpPoints = [];
  distanceM = 0;
  lastTs = -Infinity;
  lastKey = "";
  /** Last raw fix that passed all checks — used for plausibility of the next fix. */
  lastGood = null;
  /** Last smoothed point credited to the trace. */
  anchor = null;
  window = [];
  pendingRejected = [];
  /** Active (non-paused) time per tracking segment: first and last timestamp seen. */
  segmentSpans = /* @__PURE__ */ new Map();
  get anchorPoint() {
    return this.anchor;
  }
  /** Active duration (excluding pauses) in seconds. */
  get durationS() {
    let ms = 0;
    for (const s of this.segmentSpans.values()) ms += s.last - s.first;
    return ms / 1e3;
  }
  push(p) {
    const g = this.rules.gps;
    this.counters.received++;
    if (!isValidCoordinate(p) || !Number.isFinite(p.ts) || !Number.isFinite(p.accuracy)) {
      this.counters.invalidCoordinates++;
      return { accepted: false, anchored: false, rejection: "INVALID_COORDINATES" };
    }
    if (this.opts.now !== void 0 && p.ts > this.opts.now + g.futureToleranceMs) {
      this.counters.future++;
      return { accepted: false, anchored: false, rejection: "FUTURE_TIMESTAMP" };
    }
    if (this.opts.startedAt !== void 0 && p.ts < this.opts.startedAt - g.runWindowSlackMs || this.opts.endedAt !== void 0 && p.ts > this.opts.endedAt + g.runWindowSlackMs) {
      this.counters.outOfRun++;
      return { accepted: false, anchored: false, rejection: "OUT_OF_RUN" };
    }
    const key = `${p.ts}|${p.lat}|${p.lng}`;
    if (key === this.lastKey || p.ts === this.lastTs) {
      this.counters.duplicates++;
      return { accepted: false, anchored: false, rejection: "DUPLICATE" };
    }
    if (p.ts < this.lastTs) {
      this.counters.backward++;
      return { accepted: false, anchored: false, rejection: "BACKWARD_TIMESTAMP" };
    }
    this.lastTs = p.ts;
    this.lastKey = key;
    if (p.accuracy > g.maxAccuracyForDistanceM) {
      this.counters.poorAccuracy++;
      return { accepted: false, anchored: false, rejection: "POOR_ACCURACY" };
    }
    const span = this.segmentSpans.get(p.segment);
    if (span) span.last = p.ts;
    else this.segmentSpans.set(p.segment, { first: p.ts, last: p.ts });
    const last = this.lastGood;
    if (!last || last.segment !== p.segment) {
      this.flush();
      this.startSubTrace(p);
      return { accepted: true, anchored: true };
    }
    this.counters.movementCandidates++;
    const d = haversineMeters(last, p);
    const dt = (p.ts - last.ts) / 1e3;
    const effective = Math.max(0, d - 0.5 * (last.accuracy + p.accuracy));
    if (effective / dt > g.maxSpeedMps) {
      const isJump = d > g.jumpDistanceM;
      if (isJump) {
        this.counters.jumps++;
        this.jumpPoints.push(last, p);
      } else this.counters.impossibleSpeed++;
      return this.handleRejectedMovement(p, isJump ? "GPS_JUMP" : "IMPOSSIBLE_SPEED");
    }
    this.pendingRejected = [];
    this.lastGood = p;
    this.window.push(p);
    if (this.window.length > SMOOTHING_WINDOW) this.window.shift();
    const smoothed = smooth(this.window);
    const segment = this.tryExtend(smoothed, false);
    if (!segment) {
      this.counters.jitter++;
      return { accepted: false, anchored: false, rejection: "JITTER" };
    }
    return { accepted: true, anchored: true, segment };
  }
  /**
   * Credits the remaining tail of the current sub-trace (movement shorter than the
   * jitter threshold since the last anchor). Call when the run finishes or pauses.
   */
  flush() {
    if (!this.anchor || !this.lastGood || this.lastGood === this.anchor) return null;
    return this.tryExtend(this.lastGood, true);
  }
  tryExtend(p, finalizing) {
    const g = this.rules.gps;
    const anchor = this.anchor;
    const d = haversineMeters(anchor, p);
    const dt = (p.ts - anchor.ts) / 1e3;
    const minMove = finalizing ? g.minMoveM : Math.min(
      g.maxMinMoveM,
      Math.max(g.minMoveM, Math.min(anchor.accuracy, p.accuracy) * g.minMoveAccuracyFactor)
    );
    if (d < minMove || dt < 0 || dt === 0 && !finalizing) return null;
    const speedMps = dt > 0 ? d / dt : this.segments[this.segments.length - 1]?.speedMps ?? 0;
    const segment = { from: anchor, to: p, distanceM: d, durationS: dt, speedMps };
    this.segments.push(segment);
    this.distanceM += d;
    this.anchor = p;
    return segment;
  }
  startSubTrace(p) {
    this.anchor = p;
    this.lastGood = p;
    this.window = [p];
    this.pendingRejected = [];
  }
  /**
   * If the last good fix itself was the outlier, every following point looks like a
   * jump. After N consecutive rejected points that are consistent with each other,
   * start a new sub-trace on the newest one (no distance credited for the gap).
   */
  handleRejectedMovement(p, rejection) {
    this.pendingRejected.push(p);
    const n = this.rules.gps.reanchorAfter;
    if (this.pendingRejected.length >= n) {
      const recent = this.pendingRejected.slice(-n);
      let consistent = true;
      for (let i = 1; i < recent.length; i++) {
        const a = recent[i - 1];
        const b = recent[i];
        const dt = (b.ts - a.ts) / 1e3;
        const eff = Math.max(0, haversineMeters(a, b) - 0.5 * (a.accuracy + b.accuracy));
        if (dt <= 0 || eff / dt > this.rules.gps.maxSpeedMps) consistent = false;
      }
      if (consistent) {
        this.counters.reanchors++;
        this.flush();
        this.startSubTrace(p);
        return { accepted: false, anchored: true, rejection };
      }
    }
    return { accepted: false, anchored: false, rejection };
  }
};
var SMOOTHING_WINDOW = 5;
function smooth(window) {
  const latest = window[window.length - 1];
  if (window.length === 1) return latest;
  let wSum = 0;
  let lat = 0;
  let lng = 0;
  let acc = 0;
  for (const q of window) {
    const w = 1 / Math.max(1, q.accuracy);
    wSum += w;
    lat += q.lat * w;
    lng += q.lng * w;
    acc += q.accuracy;
  }
  return { ...latest, lat: lat / wSum, lng: lng / wSum, accuracy: acc / window.length };
}
function processTrace(points, opts = {}) {
  const proc = new TraceProcessor(opts);
  const sorted = [...points].sort((a, b) => a.seq - b.seq);
  for (const p of sorted) proc.push(p);
  proc.flush();
  return proc;
}
function toLatLng(p) {
  return { lat: p.lat, lng: p.lng };
}

// src/capture.ts
import { segmentCellDistances } from "@rw/geo";
function computeCellPresence(input) {
  const rules = input.rules ?? DEFAULT_RULES;
  const maxAcc = rules.gps.maxAccuracyForCaptureM;
  const cells = /* @__PURE__ */ new Map();
  const touch = (key, ts) => {
    let c = cells.get(key);
    if (!c) {
      c = { gridKey: key, distanceInsideM: 0, firstEnteredAt: ts, disqualified: false };
      cells.set(key, c);
    }
    return c;
  };
  for (const s of input.segments) {
    if (s.from.accuracy > maxAcc || s.to.accuracy > maxAcc) continue;
    const parts = segmentCellDistances(input.grid, s.from, s.to, rules.capture.sampleStepM);
    for (const [key, meters] of parts) touch(key, s.from.ts).distanceInsideM += meters;
  }
  for (const p of input.jumpPoints ?? []) {
    const key = input.grid.keyAt(p);
    const c = cells.get(key);
    if (c) c.disqualified = true;
  }
  return [...cells.values()].sort((a, b) => a.firstEnteredAt - b.firstEnteredAt);
}
function computeCaptures(input) {
  const rules = input.rules ?? DEFAULT_RULES;
  return computeCellPresence(input).filter(
    (c) => !c.disqualified && c.distanceInsideM >= rules.capture.minDistanceInsideM && (input.isPlayable ? input.isPlayable(c.gridKey) : true)
  ).map(({ gridKey, distanceInsideM, firstEnteredAt }) => ({
    gridKey,
    distanceInsideM: Math.round(distanceInsideM * 10) / 10,
    firstEnteredAt
  }));
}

// src/validation.ts
import { AreaIndex, paceSecondsPerKm } from "@rw/geo";
import {
  RunFlag,
  RunValidationStatus
} from "@rw/shared";
var SEVERITY_RANK = { info: 0, suspicious: 1, reject: 2 };
function validateRun(input) {
  const rules = input.rules ?? DEFAULT_RULES;
  const v = rules.validation;
  const trace = processTrace(input.points, {
    rules,
    now: input.now,
    startedAt: input.startedAt,
    endedAt: input.endedAt
  });
  const c = trace.counters;
  const flags = [];
  const add = (code, severity, count, message) => flags.push({ code, severity, count, message });
  const share = (n, of) => of > 0 ? n / of : 0;
  if (c.invalidCoordinates > 0) {
    add(
      RunFlag.INVALID_COORDINATES,
      share(c.invalidCoordinates, c.received) > v.suspiciousInvalidShare ? "suspicious" : "info",
      c.invalidCoordinates,
      "Points with invalid coordinates were discarded"
    );
  }
  if (c.duplicates > 0) add(RunFlag.DUPLICATE_POINTS, "info", c.duplicates, "Duplicate GPS points ignored");
  if (c.poorAccuracy > 0) {
    add(RunFlag.POOR_ACCURACY, "info", c.poorAccuracy, "Low-accuracy GPS points ignored");
    if (share(c.poorAccuracy, c.received) > v.lowQualityShare) {
      add(RunFlag.LOW_QUALITY_TRACE, "info", c.poorAccuracy, "Most of the trace had poor GPS accuracy");
    }
  }
  if (c.future > 0) {
    add(
      RunFlag.FUTURE_TIMESTAMP,
      share(c.future, c.received) > v.rejectFutureShare ? "reject" : "suspicious",
      c.future,
      "Points dated in the future"
    );
  }
  if (c.backward > 0) {
    add(
      RunFlag.BACKWARD_TIMESTAMP,
      c.backward >= v.suspiciousBackwardCount ? "suspicious" : "info",
      c.backward,
      "Points with timestamps going backwards"
    );
  }
  if (c.outOfRun > 0) {
    add(
      RunFlag.TIMESTAMP_OUT_OF_RUN,
      share(c.outOfRun, c.received) > v.suspiciousOutOfRunShare ? "suspicious" : "info",
      c.outOfRun,
      "Points outside the run time window"
    );
  }
  const rejectedMoves = c.jumps + c.impossibleSpeed;
  const rejectShare = share(rejectedMoves, c.movementCandidates);
  if (c.jumps > 0) {
    add(
      RunFlag.GPS_JUMP,
      c.jumps >= v.suspiciousJumpCount || rejectShare > v.suspiciousRejectShare ? "suspicious" : "info",
      c.jumps,
      "Sudden GPS jumps were excluded"
    );
  }
  if (c.impossibleSpeed > 0) {
    add(
      RunFlag.IMPOSSIBLE_SPEED,
      rejectShare > v.suspiciousRejectShare ? "suspicious" : "info",
      c.impossibleSpeed,
      "Movement faster than humanly possible was excluded"
    );
  }
  const durationS = trace.durationS;
  const distanceM = trace.distanceM;
  if (trace.segments.length === 0 && c.received < 2) {
    add(RunFlag.TOO_FEW_POINTS, "info", c.received, "Not enough GPS points were recorded");
  }
  const avgSpeed = durationS > 0 ? distanceM / durationS : 0;
  const burstDistance = trace.segments.filter((s) => s.speedMps > v.vehicleBurstSpeedMps).reduce((sum, s) => sum + s.distanceM, 0);
  if (distanceM > 200 && avgSpeed > v.vehicleAvgSpeedMps || distanceM > 200 && share(burstDistance, distanceM) > v.vehicleBurstShare) {
    add(RunFlag.VEHICLE_SPEED, "reject", 1, "Speed is consistent with a vehicle, not a runner");
  }
  if (trace.segments.length >= v.syntheticMinPoints) {
    const speeds = trace.segments.map((s) => s.speedMps).sort((a, b) => a - b);
    const median = speeds[Math.floor(speeds.length / 2)];
    const uniform = speeds.filter((s) => Math.abs(s - median) <= v.syntheticSpeedStdMps).length;
    const accuracies = new Set(input.points.map((p) => p.accuracy));
    const accuracyLooksFake = input.accuracyKnown === false ? true : accuracies.size === 1;
    if (uniform / speeds.length > 0.9 && accuracyLooksFake) {
      add(RunFlag.SYNTHETIC_TRACE, "suspicious", trace.segments.length, "Trace looks machine-generated");
    }
  }
  if (input.clientDistanceM !== void 0) {
    const diff = Math.abs(input.clientDistanceM - distanceM);
    if (diff > Math.max(v.clientMismatchMinM, distanceM * v.clientMismatchShare)) {
      add(
        RunFlag.CLIENT_DISTANCE_MISMATCH,
        "suspicious",
        Math.round(diff),
        "Client-reported distance does not match the recorded trace"
      );
    }
  }
  let regionId = null;
  if (input.regions?.length && distanceM > 0) {
    const best = pickRegion(trace.segments, input.regions);
    if (best && best.share >= v.minInsideRegionShare) regionId = best.id;
    else add(RunFlag.OUTSIDE_REGION, "reject", Math.round((1 - (best?.share ?? 0)) * 100), "Most of the run is outside the game area");
  }
  const worst = flags.reduce((m, f) => Math.max(m, SEVERITY_RANK[f.severity]), 0);
  const status = worst === 2 ? RunValidationStatus.REJECTED : worst === 1 ? RunValidationStatus.SUSPICIOUS : RunValidationStatus.VALID;
  return {
    status,
    flags,
    distanceM: Math.round(distanceM * 10) / 10,
    durationS: Math.round(durationS),
    paceSecPerKm: paceSecondsPerKm(distanceM, durationS),
    trace,
    regionId
  };
}
function pickRegion(segments, regions) {
  let best = null;
  for (const r of regions) {
    const share = insideRegionShare(segments, r.area);
    if (!best || share > best.share) best = { id: r.id, share };
  }
  return best;
}
function insideRegionShare(segments, region) {
  const area = new AreaIndex(region);
  let total = 0;
  let inside = 0;
  for (const s of segments) {
    total += s.distanceM;
    const mid = { lat: (s.from.lat + s.to.lat) / 2, lng: (s.from.lng + s.to.lng) / 2 };
    if (area.contains(mid)) inside += s.distanceM;
  }
  return total > 0 ? inside / total : 0;
}

// src/xp.ts
import { RunValidationStatus as RunValidationStatus2, XpEventType } from "@rw/shared";
function isCompletedRun(distanceM, durationS, rules = DEFAULT_RULES) {
  return distanceM >= rules.xp.completedMinDistanceM && durationS >= rules.xp.completedMinDurationS;
}
function computeRunXp(input) {
  const rules = input.rules ?? DEFAULT_RULES;
  if (input.status !== RunValidationStatus2.VALID) return [];
  const items = [];
  const distanceXp = Math.floor(input.distanceM / 1e3 * rules.xp.perKm);
  if (distanceXp > 0) {
    items.push({
      type: XpEventType.DISTANCE,
      amount: distanceXp,
      label: `${(input.distanceM / 1e3).toFixed(2)} km validated`
    });
  }
  if (input.captureCount > 0) {
    items.push({
      type: XpEventType.TERRITORY_CAPTURE,
      amount: input.captureCount * rules.xp.territoryCapture,
      label: `${input.captureCount} territor${input.captureCount === 1 ? "y" : "ies"} captured`
    });
  }
  if (isCompletedRun(input.distanceM, input.durationS, rules)) {
    items.push({ type: XpEventType.RUN_COMPLETED, amount: rules.xp.completedRun, label: "Run completed" });
    if (input.isFirstRunOfDay) {
      items.push({
        type: XpEventType.FIRST_DAILY_RUN,
        amount: rules.xp.firstDailyRun,
        label: "First run of the day"
      });
    }
  }
  return items;
}

// src/streak.ts
function localDayKey(ts, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return fmt.format(typeof ts === "number" ? new Date(ts) : ts);
}
function dayNumber(dayKey) {
  const [y, m, d] = dayKey.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 864e5);
}
function applyRunToStreak(state, runDay) {
  if (state.lastRunDay === runDay) return state;
  let current = 1;
  if (state.lastRunDay) {
    const gap = dayNumber(runDay) - dayNumber(state.lastRunDay);
    if (gap < 0) return state;
    if (gap === 1) current = state.currentStreak + 1;
  }
  return { currentStreak: current, bestStreak: Math.max(state.bestStreak, current), lastRunDay: runDay };
}
function effectiveStreak(state, today) {
  if (!state.lastRunDay) return 0;
  const gap = dayNumber(today) - dayNumber(state.lastRunDay);
  return gap <= 1 ? state.currentStreak : 0;
}

// src/simulate.ts
import { destinationPoint, haversineMeters as haversineMeters2, bearingDegrees } from "@rw/geo";
function seededRandom(seed) {
  let a = seed >>> 0;
  return () => {
    a = a + 1831565813 >>> 0;
    let t = a;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function simulateTrace(waypoints, opts) {
  const rnd = seededRandom(opts.seed ?? 1);
  const speed = opts.speedMps ?? 3;
  const interval = opts.intervalMs ?? 1e3;
  const noise = opts.noiseM ?? 1.5;
  const [accMin, accMax] = opts.accuracy ?? [4, 12];
  const segment = opts.segment ?? 0;
  let seq = opts.startSeq ?? 0;
  const points = [];
  let ts = opts.startTs;
  const emit = (p) => {
    const jittered = destinationPoint(p, rnd() * 360, rnd() * noise);
    const v = speed * (0.9 + rnd() * 0.2);
    points.push({
      seq: seq++,
      segment,
      lat: jittered.lat,
      lng: jittered.lng,
      ts,
      accuracy: Math.round((accMin + rnd() * (accMax - accMin)) * 10) / 10,
      altitude: 5 + rnd(),
      speed: Math.round(v * 100) / 100
    });
    ts += interval;
  };
  if (waypoints.length === 0) return points;
  let cur = waypoints[0];
  emit(cur);
  for (let i = 1; i < waypoints.length; i++) {
    const target = waypoints[i];
    while (haversineMeters2(cur, target) > 1e-6) {
      const step = speed * (interval / 1e3) * (0.9 + rnd() * 0.2);
      const remaining = haversineMeters2(cur, target);
      cur = remaining <= step ? target : destinationPoint(cur, bearingDegrees(cur, target), step);
      emit(cur);
    }
  }
  return points;
}

// src/pace.ts
function computeSplits(segments, splitM = 1e3, minPartialM = 100) {
  const splits = [];
  let dist = 0;
  let time = 0;
  for (const s of segments) {
    let segDist = s.distanceM;
    let segTime = s.durationS;
    while (dist + segDist >= splitM) {
      const need = splitM - dist;
      const frac = segDist > 0 ? need / segDist : 0;
      const t = time + segTime * frac;
      splits.push({ index: splits.length + 1, distanceM: splitM, durationS: round1(t), paceSecPerKm: round1(t / (splitM / 1e3)), complete: true });
      segDist -= need;
      segTime -= segTime * frac;
      dist = 0;
      time = 0;
    }
    dist += segDist;
    time += segTime;
  }
  if (dist >= minPartialM && time > 0) {
    splits.push({
      index: splits.length + 1,
      distanceM: Math.round(dist),
      durationS: round1(time),
      paceSecPerKm: round1(time / (dist / 1e3)),
      complete: false
    });
  }
  return splits;
}
function currentPace(segments, nowTs, opts = {}) {
  const windowS = opts.windowS ?? 30;
  const minDistanceM = opts.minDistanceM ?? 20;
  const stoppedAfterS = opts.stoppedAfterS ?? 12;
  const last = segments[segments.length - 1];
  if (!last || (nowTs - last.to.ts) / 1e3 > stoppedAfterS) return null;
  let dist = 0;
  let time = 0;
  for (let i = segments.length - 1; i >= 0; i--) {
    const s = segments[i];
    if (i < segments.length - 1 && segments[i + 1].from !== s.to) break;
    dist += s.distanceM;
    time += s.durationS;
    if (time >= windowS) break;
  }
  time += Math.max(0, (nowTs - last.to.ts) / 1e3);
  if (dist < minDistanceM || time <= 0) return null;
  return round1(time / (dist / 1e3));
}
var round1 = (n) => Math.round(n * 10) / 10;

// src/events.ts
var km = (m) => m < 1e3 ? `${Math.round(m)} m` : `${+(m / 1e3).toFixed(2)} km`;
var pace = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}/km`;
function eventRunEligibility(rules, run, w) {
  const no = (reason) => ({ eligible: false, reason });
  if (run.status !== "VALID") return no("Run was not validated");
  if (run.startedAt < w.startsAt || run.startedAt >= w.endsAt) return no("Outside the event dates");
  if (run.startedAt < w.joinedAt) return no("Started before you joined the event");
  if (rules.minRunDistanceM && run.distanceM < rules.minRunDistanceM) return no(`Shorter than ${km(rules.minRunDistanceM)}`);
  if (rules.maxRunDistanceM && run.distanceM > rules.maxRunDistanceM) return no(`Longer than ${km(rules.maxRunDistanceM)}`);
  if (rules.minPaceSecPerKm || rules.maxPaceSecPerKm) {
    if (run.paceSecPerKm === null) return no("Pace could not be measured");
    if (rules.minPaceSecPerKm && run.paceSecPerKm < rules.minPaceSecPerKm) return no(`Faster than ${pace(rules.minPaceSecPerKm)}`);
    if (rules.maxPaceSecPerKm && run.paceSecPerKm > rules.maxPaceSecPerKm) return no(`Slower than ${pace(rules.maxPaceSecPerKm)}`);
  }
  return { eligible: true, reason: null };
}
function isQualified(rules, t) {
  if (rules.minTotalDistanceM && t.distanceM < rules.minTotalDistanceM) return false;
  if (rules.minRuns && t.runs < rules.minRuns) return false;
  if (rules.minSectors && t.sectors < rules.minSectors) return false;
  return true;
}
function rankDescending(items, value) {
  const sorted = [...items].sort((a, b) => value(b) - value(a));
  let rank = 0;
  return sorted.map((item, i) => {
    if (i === 0 || value(sorted[i - 1]) !== value(item)) rank = i + 1;
    return { ...item, rank };
  });
}
function takesEventCell(cur, runner, capturedAt) {
  if (!cur) return true;
  if (cur.ownerId === runner.userId) return false;
  if (runner.teamId && cur.teamId === runner.teamId) return false;
  return cur.capturedAt <= capturedAt;
}
export {
  DEFAULT_RULES,
  REVOCABLE_BADGES,
  TraceProcessor,
  applyRunToStreak,
  badgeTier,
  badgeTiers,
  computeCaptures,
  computeCellPresence,
  computeRunXp,
  computeSplits,
  createRules,
  currentPace,
  effectiveStreak,
  eventRunEligibility,
  insideRegionShare,
  isCompletedRun,
  isQualified,
  levelForXp,
  levelProgress,
  localDayKey,
  pickRegion,
  processTrace,
  rankDescending,
  rankForLevel,
  rankUp,
  seededRandom,
  simulateTrace,
  takesEventCell,
  toLatLng,
  totalXpForLevel,
  validateRun,
  xpToCompleteLevel
};
//# sourceMappingURL=index.js.map